import DOMPurify from 'dompurify';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Check, Zap, Shield, TrendingUp, Users, FileText, Calendar, Star, ChevronDown, DollarSign, Lock, Award, BarChart3, Activity, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchPublicLandingContent, type LandingPageContentData } from '../../superadmin/hooks/useLandingCms';

interface LandingPageProps {
  /** CMS content passed from the editor (preview mode). If not provided, fetches from API. */
  content?: LandingPageContentData | null;
}

// Enhanced Dashboard Mockup with 3D Tilt Effect
const DashboardMockup = () => {
  const { t } = useTranslation();
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;

    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;

    const rotateY = (mouseX / rect.width) * 15;
    const rotateX = -(mouseY / rect.height) * 15;

    setRotation({ x: rotateX, y: rotateY });
  };

  const handleMouseLeave = () => {
    setRotation({ x: 0, y: 0 });
  };

  return (
    <div
      className="fade-up-element opacity-0 translate-y-8 transition-all duration-700"
      style={{transitionDelay: '500ms'}}
    >
      <div
        className="relative"
        style={{ perspective: '1500px' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Enhanced Glow effect with animation */}
        <div className="absolute -inset-4 bg-gradient-lime opacity-20 blur-3xl rounded-3xl animate-pulse-lime" />

        {/* Mockup Card with 3D Transform */}
        <div
          ref={cardRef}
          className="relative bg-white/98 backdrop-blur-md rounded-3xl shadow-2xl border border-white/80 overflow-hidden transition-all duration-300 ease-out"
          style={{
            transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg) translateZ(20px)`,
            transformStyle: 'preserve-3d'
          }}
        >
          {/* Header with Mini Stats */}
          <div className="flex items-center justify-between px-8 py-5 bg-gradient-to-r from-teal-50 via-white to-lime-50/50 border-b border-gray-100">
            <div className="flex items-center gap-4">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
              </div>

              {/* Mini stats in header */}
              <div className="flex items-center gap-3 ml-4">
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-teal-50 border border-teal-100">
                  <BarChart3 size={12} className="text-teal-600" />
                  <span className="text-xs font-semibold text-teal-600">+24%</span>
                </div>
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-lime-50 border border-lime-200">
                  <Activity size={12} className="text-lime-600" />
                  <span className="text-xs font-semibold text-lime-700">Live</span>
                </div>
              </div>
            </div>
            <span className="text-sm font-bold text-teal-600 tracking-wide">{t('landing.mockup.dashboardTitle')}</span>
            <div className="w-24" />
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Top Row - Stats Grid */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              {/* Main Stat - Revenue */}
              <div className="col-span-2 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-6 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -translate-y-8 translate-x-8" />
                <div className="relative z-10">
                  <div className="text-xs font-light tracking-widest uppercase opacity-90 mb-2">{t('landing.mockup.monthlyRevenue')}</div>
                  <div className="text-4xl font-bold mb-3">{t('landing.mockup.revenueAmount')}</div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-lime-500 text-teal-900 shadow-lime">
                    <TrendingUp size={14} strokeWidth={2.5} />
                    <span className="text-xs font-bold">{t('landing.mockup.thisMonth')}</span>
                  </div>
                </div>
              </div>

              {/* Side Stats */}
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-lime-50 to-lime-100/50 rounded-xl p-4 border border-lime-200/50">
                  <div className="flex items-center justify-between mb-2">
                    <CreditCard size={16} className="text-lime-600" />
                    <span className="text-xs font-semibold text-lime-700">+8</span>
                  </div>
                  <div className="text-lg font-bold text-teal-600">47</div>
                  <div className="text-xs text-teal-600/70">{t('landing.mockup.paidInvoices')}</div>
                </div>

                <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-xl p-4 border border-teal-200/50">
                  <div className="flex items-center justify-between mb-2">
                    <FileText size={16} className="text-teal-600" />
                    <span className="text-xs font-semibold text-teal-700">+12</span>
                  </div>
                  <div className="text-lg font-bold text-teal-600">23</div>
                  <div className="text-xs text-teal-600/70">{t('landing.mockup.ongoingQuotes')}</div>
                </div>
              </div>
            </div>

            {/* Chart Section */}
            <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-sm font-semibold text-teal-600 mb-1">{t('landing.mockup.weeklyEvolution')}</h4>
                  <p className="text-xs text-gray-500">{t('landing.mockup.dailyPerformance')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-teal-500" />
                  <span className="text-xs text-gray-600">{t('landing.mockup.revenues')}</span>
                </div>
              </div>

              {/* Enhanced Chart with grid */}
              <div className="relative">
                {/* Grid lines */}
                <div className="absolute inset-0 flex flex-col justify-between">
                  <div className="border-t border-gray-200" />
                  <div className="border-t border-gray-200" />
                  <div className="border-t border-gray-200" />
                </div>

                {/* Bars */}
                <div className="flex items-end gap-3 h-40 relative">
                  {[
                    { height: 40, label: t('landing.mockup.days.mon'), value: '2.8k' },
                    { height: 65, label: t('landing.mockup.days.tue'), value: '4.2k' },
                    { height: 80, label: t('landing.mockup.days.wed'), value: '5.1k' },
                    { height: 55, label: t('landing.mockup.days.thu'), value: '3.6k' },
                    { height: 90, label: t('landing.mockup.days.fri'), value: '6.2k' },
                    { height: 75, label: t('landing.mockup.days.sat'), value: '4.8k' },
                    { height: 100, label: t('landing.mockup.days.sun'), value: '7.3k', highlight: true }
                  ].map((bar, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-2">
                      <div className="w-full relative group">
                        {bar.highlight ? (
                          <>
                            <div
                              className="w-full bg-gradient-lime rounded-t-lg shadow-lime relative transition-all duration-300 group-hover:scale-105"
                              style={{height: `${bar.height * 1.6}px`}}
                            >
                              <div className="absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg bg-lime-500 text-teal-900 text-xs font-bold shadow-lime-lg whitespace-nowrap">
                                {bar.value} • {bar.label}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div
                            className="w-full bg-gradient-to-t from-teal-500 to-teal-400 rounded-t-lg transition-all duration-300 group-hover:scale-105 group-hover:from-teal-600 group-hover:to-teal-500"
                            style={{height: `${bar.height * 1.6}px`, opacity: 0.4 + (i * 0.1)}}
                          />
                        )}
                      </div>
                      <span className="text-xs text-gray-500 font-medium">{bar.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LandingPage: React.FC<LandingPageProps> = ({ content: propsContent }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [scrolled, setScrolled] = useState(false);

  // Fetch published CMS content from API (skipped in preview mode)
  const { data: apiContent } = useQuery({
    queryKey: ['landing-content'],
    queryFn: fetchPublicLandingContent,
    enabled: !propsContent,
    staleTime: 5 * 60 * 1000,
  });

  const cmsContent = propsContent || apiContent;

  /**
   * Helper: get CMS value or fall back to i18n translation.
   * For nested CMS data, pass the path within the section.
   */
  const cms = (section: string, key: string, i18nKey: string): string => {
    if (cmsContent) {
      const sectionData = (cmsContent as any)[section];
      if (sectionData) {
        // Support nested keys like "items.0.title"
        const keys = key.split('.');
        let val: any = sectionData;
        for (const k of keys) {
          if (val == null) break;
          val = val[k];
        }
        if (typeof val === 'string' && val.length > 0) return val;
      }
    }
    return t(i18nKey);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    );

    document.querySelectorAll('.fade-up-element').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleLoginClick = () => navigate('/login');
  const handleSignupClick = () => navigate('/register');

  // === CMS-aware data for array-based sections (with i18n fallback) ===

  const statsItems = (() => {
    const cmsStats = cmsContent?.stats;
    const defaultIcons = [DollarSign, Lock, Award];
    if (Array.isArray(cmsStats) && cmsStats.length > 0) {
      return (cmsStats as any[]).map((s, i) => ({
        icon: defaultIcons[i] || DollarSign,
        number: s.value || '',
        text: s.text || '',
        featured: i === 1,
      }));
    }
    return [
      { icon: DollarSign, number: t('landing.stats.savingsPercent'), text: t('landing.stats.savingsText'), featured: false },
      { icon: Lock, number: t('landing.stats.archiveDuration'), text: t('landing.stats.archiveText'), featured: true },
      { icon: Award, number: t('landing.stats.isoLabel'), text: t('landing.stats.isoText'), featured: false },
    ];
  })();

  const featuresItems = (() => {
    const cmsFeatures = cmsContent?.features as any;
    const defaultIcons = [FileText, Users, Shield, TrendingUp];
    if (cmsFeatures?.items && Array.isArray(cmsFeatures.items) && cmsFeatures.items.length > 0) {
      return cmsFeatures.items.map((f: any, i: number) => ({
        icon: defaultIcons[i] || FileText,
        title: f.title || '',
        text: f.text || '',
      }));
    }
    return [
      { icon: FileText, title: t('landing.features.quotesInvoices.title'), text: t('landing.features.quotesInvoices.text') },
      { icon: Users, title: t('landing.features.clientManagement.title'), text: t('landing.features.clientManagement.text') },
      { icon: Shield, title: t('landing.features.legalCompliance.title'), text: t('landing.features.legalCompliance.text') },
      { icon: TrendingUp, title: t('landing.features.accountingExport.title'), text: t('landing.features.accountingExport.text') },
    ];
  })();

  const howItWorksDefaultImages = [
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&q=80',
    'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=600&q=80',
    'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&q=80',
  ];
  const howItWorksSteps = (() => {
    const cmsHiw = cmsContent?.howItWorks as any;
    const stepIcons = [FileText, Calendar, Shield];
    if (cmsHiw?.steps && Array.isArray(cmsHiw.steps) && cmsHiw.steps.length > 0) {
      return cmsHiw.steps.map((s: any, i: number) => ({
        number: String(i + 1).padStart(2, '0'),
        icon: stepIcons[i] || FileText,
        title: s.title || '',
        text: s.text || '',
        image: s.imageUrl || howItWorksDefaultImages[i] || '',
      }));
    }
    return [
      { number: '01', icon: FileText, title: t('landing.howItWorks.step1.title'), text: t('landing.howItWorks.step1.text'), image: howItWorksDefaultImages[0] },
      { number: '02', icon: Calendar, title: t('landing.howItWorks.step2.title'), text: t('landing.howItWorks.step2.text'), image: howItWorksDefaultImages[1] },
      { number: '03', icon: Shield, title: t('landing.howItWorks.step3.title'), text: t('landing.howItWorks.step3.text'), image: howItWorksDefaultImages[2] },
    ];
  })();

  const pricingPlans = (() => {
    const cmsPricing = cmsContent?.pricing as any;
    if (cmsPricing?.plans && Array.isArray(cmsPricing.plans) && cmsPricing.plans.length > 0) {
      return cmsPricing.plans.map((p: any) => ({
        name: p.name || '',
        description: p.description || '',
        priceMonthly: String(p.priceMonthly ?? ''),
        priceAnnual: String(p.priceAnnual ?? ''),
        savings: p.savings || '',
        badge: p.popular ? (cmsPricing.popularLabel || t('landing.pricing.popular')) : undefined,
        features: Array.isArray(p.features) ? p.features : [],
        cta: p.cta || '',
        featured: !!p.popular,
      }));
    }
    return [
      {
        name: t('landing.pricing.starter.name'), description: t('landing.pricing.starter.description'),
        priceMonthly: '37', priceAnnual: '29', savings: t('landing.pricing.starter.savings'),
        features: [
          t('landing.pricing.starter.features.unlimitedQuotesInvoices'), t('landing.pricing.starter.features.unlimitedClients'),
          t('landing.pricing.starter.features.autoTreasury'), t('landing.pricing.starter.features.facturXChorus'),
          t('landing.pricing.starter.features.paymentsReminders'), t('landing.pricing.starter.features.legalNumbering'),
          t('landing.pricing.starter.features.emailSupport'),
        ],
        cta: t('landing.pricing.starter.cta'), featured: false,
      },
      {
        name: t('landing.pricing.pro.name'), description: t('landing.pricing.pro.description'),
        priceMonthly: '59', priceAnnual: '49', savings: t('landing.pricing.pro.savings'),
        badge: t('landing.pricing.popular'),
        features: [
          t('landing.pricing.pro.features.allStarter'), t('landing.pricing.pro.features.multiUsers'),
          t('landing.pricing.pro.features.accountingExport'), t('landing.pricing.pro.features.bankReconciliation'),
          t('landing.pricing.pro.features.expensesOcr'), t('landing.pricing.pro.features.preAccounting'),
          t('landing.pricing.pro.features.advancedReminders'), t('landing.pricing.pro.features.onlinePayment'),
          t('landing.pricing.pro.features.eSignature'), t('landing.pricing.pro.features.probatoryArchive'),
          t('landing.pricing.pro.features.prioritySupport'),
        ],
        cta: t('landing.pricing.pro.cta'), featured: true,
      },
      {
        name: t('landing.pricing.enterprise.name'), description: t('landing.pricing.enterprise.description'),
        priceMonthly: '119', priceAnnual: '99', savings: t('landing.pricing.enterprise.savings'),
        features: [
          t('landing.pricing.enterprise.features.allPro'), t('landing.pricing.enterprise.features.multiUsers10'),
          t('landing.pricing.enterprise.features.unlimitedCompanies'), t('landing.pricing.enterprise.features.crmAddon'),
          t('landing.pricing.enterprise.features.erpAddon'), t('landing.pricing.enterprise.features.publicApi'),
          t('landing.pricing.enterprise.features.customWebhooks'), t('landing.pricing.enterprise.features.sla'),
          t('landing.pricing.enterprise.features.prioritySupport'), t('landing.pricing.enterprise.features.dedicatedOnboarding'),
          t('landing.pricing.enterprise.features.teamTraining'),
        ],
        cta: t('landing.pricing.enterprise.cta'), featured: false,
      },
    ];
  })();

  const testimonialsItems = (() => {
    const cmsTest = cmsContent?.testimonials as any;
    if (cmsTest?.items && Array.isArray(cmsTest.items) && cmsTest.items.length > 0) {
      return cmsTest.items.map((item: any) => ({
        rating: item.rating || 5,
        text: item.text || '',
        name: item.name || '',
        role: item.role || '',
      }));
    }
    return [
      { rating: 5, text: t('landing.testimonials.testimonial1.text'), name: t('landing.testimonials.testimonial1.name'), role: t('landing.testimonials.testimonial1.role') },
      { rating: 5, text: t('landing.testimonials.testimonial2.text'), name: t('landing.testimonials.testimonial2.name'), role: t('landing.testimonials.testimonial2.role') },
      { rating: 5, text: t('landing.testimonials.testimonial3.text'), name: t('landing.testimonials.testimonial3.name'), role: t('landing.testimonials.testimonial3.role') },
    ];
  })();

  const teamData = (() => {
    const cmsTeam = cmsContent?.team as any;
    return {
      bgImage: cmsTeam?.bgImageUrl || 'https://images.unsplash.com/photo-1531973576160-7125cd663d86?w=1400&q=80',
      mainImage: cmsTeam?.imageUrl || 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80',
      supportTime: cmsTeam?.supportTime || t('landing.team.supportTime'),
      supportLabel: cmsTeam?.supportLabel || t('landing.team.supportLabel'),
      clientsCount: cmsTeam?.clientsCount || t('landing.team.clientsCount'),
      clientsLabel: cmsTeam?.clientsLabel || t('landing.team.clientsLabel'),
    };
  })();

  return (
    <div className="min-h-screen bg-white font-sans antialiased">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-[1000] transition-all duration-300 ${
        scrolled
          ? 'bg-teal-600/95 backdrop-blur-xl shadow-lg border-b border-lime-500/20'
          : 'bg-teal-600/85 backdrop-blur-md border-b border-lime-500/10'
      }`}>
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center gap-3 group cursor-pointer">
              <div className="w-11 h-11 rounded-xl bg-gradient-lime flex items-center justify-center shadow-lime transition-all group-hover:shadow-lime-lg group-hover:scale-105">
                <FileText size={24} strokeWidth={2.5} className="text-teal-600" />
              </div>
              <span className="text-white text-2xl font-bold tracking-tight">FacturXPro</span>
            </div>

            {/* Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-white/90 hover:text-lime-500 font-medium transition-colors">
                {t('landing.nav.features')}
              </a>
              <a href="#how-it-works" className="text-white/90 hover:text-lime-500 font-medium transition-colors">
                {t('landing.nav.howItWorks')}
              </a>
              <a href="#pricing" className="text-white/90 hover:text-lime-500 font-medium transition-colors">
                {t('landing.nav.pricing')}
              </a>
              <a href="#testimonials" className="text-white/90 hover:text-lime-500 font-medium transition-colors">
                {t('landing.nav.testimonials')}
              </a>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleLoginClick}
                className="hidden sm:block px-5 py-2.5 rounded-xl text-white/90 font-semibold hover:bg-white/10 transition-all"
              >
                {t('landing.nav.login')}
              </button>
              <button
                onClick={handleSignupClick}
                className="px-5 py-2.5 rounded-xl bg-gradient-lime text-teal-600 font-semibold shadow-lime hover:shadow-lime-lg hover:scale-105 transition-all flex items-center gap-2"
              >
                {t('landing.nav.freeTrial')}
                <ArrowRight size={18} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-24 bg-gradient-teal relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(212,255,0,0.08),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(212,255,0,0.05),transparent_50%)]" />
        <div className="absolute inset-0 opacity-[0.015] bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]" />
        {/* Team collaboration background */}
        <div className="absolute inset-0 opacity-[0.08]">
          <img
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Hero Text */}
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-lime-500/30 mb-8 fade-up-element opacity-0 translate-y-8 transition-all duration-700">
                <Zap size={16} strokeWidth={2.5} className="text-lime-500" />
                <span className="text-sm font-semibold text-white/95">{cms('hero', 'badge', 'landing.hero.badge')}</span>
              </div>

              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-[1.1] tracking-tight fade-up-element opacity-0 translate-y-8 transition-all duration-700" style={{transitionDelay: '100ms'}}>
                {cms('hero', 'titleLine1', 'landing.hero.titleLine1')}<br />
                {cms('hero', 'titleLine2', 'landing.hero.titleLine2')} <span className="text-lime-500 relative inline-block">
                  {cms('hero', 'titleHighlight', 'landing.hero.titleHighlight')}
                  <span className="absolute bottom-0 left-0 w-full h-1 bg-lime-500/30 blur-sm" />
                </span>
              </h1>

              <p className="text-xl text-white/80 mb-8 leading-relaxed max-w-2xl fade-up-element opacity-0 translate-y-8 transition-all duration-700" style={{transitionDelay: '200ms'}}>
                {cms('hero', 'subtitle', 'landing.hero.subtitle')}
              </p>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-12 fade-up-element opacity-0 translate-y-8 transition-all duration-700" style={{transitionDelay: '300ms'}}>
                <button
                  onClick={handleSignupClick}
                  className="px-8 py-4 rounded-xl bg-gradient-lime text-teal-600 font-bold text-lg shadow-lime-lg hover:shadow-lime hover:scale-105 transition-all flex items-center gap-2"
                >
                  {cms('hero', 'ctaStart', 'landing.hero.ctaStart')}
                  <ArrowRight size={20} strokeWidth={2.5} />
                </button>
                <button className="px-8 py-4 rounded-xl bg-white/10 backdrop-blur-md text-white font-semibold hover:bg-white/20 transition-all flex items-center gap-2 border border-white/20">
                  {cms('hero', 'ctaDemo', 'landing.hero.ctaDemo')}
                  <ChevronDown size={20} strokeWidth={2} />
                </button>
              </div>

              <div className="flex items-center gap-6 fade-up-element opacity-0 translate-y-8 transition-all duration-700" style={{transitionDelay: '400ms'}}>
                <div className="flex -space-x-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-lime-400 to-lime-600 border-2 border-teal-600" />
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-300 to-teal-500 border-2 border-teal-600" />
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-lime-300 to-teal-400 border-2 border-teal-600" />
                  <div className="w-10 h-10 rounded-full bg-teal-700/50 backdrop-blur-md border-2 border-teal-600 flex items-center justify-center">
                    <span className="text-xs font-bold text-white">+920</span>
                  </div>
                </div>
                <p className="text-sm text-white/70" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cms('hero', 'trustText', 'landing.hero.trustText')) }} />
              </div>
            </div>

            {/* Hero Mockup - Enhanced with 3D Tilt Effect */}
            <DashboardMockup />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {statsItems.map((stat, i) => (
              <div
                key={i}
                className={`fade-up-element opacity-0 translate-y-8 transition-all duration-700 p-8 rounded-3xl border ${
                  stat.featured
                    ? 'bg-gradient-teal border-lime-500/30 shadow-lime text-white scale-105'
                    : 'bg-white border-gray-100 shadow-lg hover:shadow-xl'
                } hover:-translate-y-1 transition-all`}
                style={{transitionDelay: `${i * 100}ms`}}
              >
                <div className={`w-16 h-16 rounded-2xl ${
                  stat.featured ? 'bg-lime-500/20' : 'bg-gradient-lime'
                } flex items-center justify-center mb-6 shadow-lg`}>
                  <stat.icon size={32} strokeWidth={2} className={stat.featured ? 'text-lime-500' : 'text-teal-600'} />
                </div>
                <div className={`text-4xl font-bold mb-3 ${stat.featured ? 'text-white' : 'text-teal-600'}`}>
                  {stat.number}
                </div>
                <div className={`text-sm leading-relaxed ${stat.featured ? 'text-white/90' : 'text-gray-600'}`}>
                  {stat.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gradient-teal text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,255,0,0.05),transparent_70%)]" />

        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <div className="text-center mb-16 fade-up-element opacity-0 translate-y-8 transition-all duration-700">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              {cms('features', 'title', 'landing.features.title')}<br />
              <span className="text-lime-500">{cms('features', 'titleHighlight', 'landing.features.titleHighlight')}</span>
            </h2>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              {cms('features', 'subtitle', 'landing.features.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuresItems.map((feature, i) => (
              <div
                key={i}
                className="fade-up-element opacity-0 translate-y-8 transition-all duration-700 p-8 rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 hover:-translate-y-2 hover:shadow-2xl group"
                style={{transitionDelay: `${i * 100}ms`}}
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-lime flex items-center justify-center mb-6 shadow-lime group-hover:shadow-lime-lg group-hover:scale-110 transition-all">
                  <feature.icon size={28} strokeWidth={2} className="text-teal-600" />
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-white/70 leading-relaxed">{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16 fade-up-element opacity-0 translate-y-8 transition-all duration-700">
            <h2 className="text-4xl md:text-5xl font-bold text-teal-600 mb-4">
              {cms('howItWorks', 'title', 'landing.howItWorks.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {cms('howItWorks', 'subtitle', 'landing.howItWorks.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {howItWorksSteps.map((step, i) => (
              <div
                key={i}
                className="fade-up-element opacity-0 translate-y-8 transition-all duration-700 relative"
                style={{transitionDelay: `${i * 100}ms`}}
              >
                {/* Connector Line */}
                {i < 2 && (
                  <div className="hidden md:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-teal-600 to-teal-300 -translate-y-1/2 z-0" />
                )}

                <div className="relative bg-white rounded-3xl overflow-hidden border-2 border-teal-100 hover:border-lime-500 hover:shadow-xl transition-all group">
                  {/* Number Badge */}
                  <div className="absolute top-4 left-4 z-10 w-12 h-12 rounded-xl bg-gradient-lime shadow-lime flex items-center justify-center">
                    <span className="text-teal-600 font-bold text-lg">{step.number}</span>
                  </div>

                  {/* Illustration Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-white via-white/20 to-transparent" />
                  </div>

                  <div className="p-8">
                    {/* Icon */}
                    <div className="w-16 h-16 rounded-2xl bg-gradient-teal flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-all">
                      <step.icon size={32} strokeWidth={2} className="text-white" />
                    </div>

                    <h3 className="text-2xl font-bold text-teal-600 mb-3">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why FacturXPro - Advantages Grid */}
      <section id="why-facturxpro" className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16 fade-up-element opacity-0 translate-y-8 transition-all duration-700">
            <h2 className="text-4xl md:text-5xl font-bold text-teal-600 mb-4">
              {t('landing.comparison.title')} <span className="text-lime-600">{t('landing.comparison.titleHighlight')}</span> ?
            </h2>
            <p className="text-xl text-gray-600">
              {t('landing.comparison.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: DollarSign, key: 'pricing' },
              { icon: Zap, key: 'facturX' },
              { icon: Lock, key: 'archive' },
              { icon: TrendingUp, key: 'btp' },
              { icon: Users, key: 'terrain' },
              { icon: Shield, key: 'compliance' },
            ].map((item, i) => (
              <div
                key={i}
                className="fade-up-element opacity-0 translate-y-8 transition-all duration-700 bg-white rounded-2xl p-8 border border-gray-100 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all"
                style={{transitionDelay: `${i * 80}ms`}}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-lime flex items-center justify-center shadow-lime flex-shrink-0">
                    <item.icon size={24} strokeWidth={2} className="text-teal-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Check size={18} strokeWidth={3} className="text-lime-600" />
                      <h3 className="text-lg font-bold text-teal-600">
                        {t(`landing.comparison.advantages.${item.key}.title`)}
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {t(`landing.comparison.advantages.${item.key}.text`)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 bg-gradient-teal text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,255,0,0.05),transparent_70%)]" />

        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <div className="text-center mb-12 fade-up-element opacity-0 translate-y-8 transition-all duration-700">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              {cms('pricing', 'title', 'landing.pricing.title')}
            </h2>
            <p className="text-xl text-white/70">
              {cms('pricing', 'subtitle', 'landing.pricing.subtitle')}
            </p>
          </div>

          {/* Billing Toggle */}
          <div className="flex justify-center mb-16 fade-up-element opacity-0 translate-y-8 transition-all duration-700" style={{transitionDelay: '100ms'}}>
            <div className="inline-flex items-center gap-1 p-1.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/20">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  billingCycle === 'monthly' ? 'bg-white text-teal-600 shadow-lg' : 'text-white hover:bg-white/10'
                }`}
              >
                {t('landing.pricing.monthly')}
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                  billingCycle === 'annual' ? 'bg-white text-teal-600 shadow-lg' : 'text-white hover:bg-white/10'
                }`}
              >
                {t('landing.pricing.annual')}
                <span className="px-2 py-0.5 rounded-full bg-lime-500 text-teal-600 text-xs font-bold">
                  {t('landing.pricing.annualDiscount')}
                </span>
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, i) => (
              <div
                key={i}
                className={`fade-up-element opacity-0 translate-y-8 transition-all duration-700 relative ${
                  plan.featured ? 'md:-mt-4 md:scale-105' : ''
                }`}
                style={{transitionDelay: `${(i + 2) * 100}ms`}}
              >
                {/* Popular Badge */}
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-lime-500 text-teal-600 text-sm font-bold shadow-lime-lg">
                    {plan.badge}
                  </div>
                )}

                <div className={`h-full rounded-3xl p-8 ${
                  plan.featured
                    ? 'bg-white text-gray-900 shadow-2xl border-2 border-lime-500'
                    : 'bg-white/10 backdrop-blur-md border border-white/20'
                }`}>
                  {/* Header */}
                  <div className="mb-6">
                    <h3 className={`text-2xl font-bold mb-2 ${plan.featured ? 'text-teal-600' : 'text-white'}`}>
                      {plan.name}
                    </h3>
                    <p className={plan.featured ? 'text-gray-600' : 'text-white/70'}>
                      {plan.description}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className={`text-5xl font-bold ${plan.featured ? 'text-teal-600' : 'text-white'}`}>
                        {billingCycle === 'monthly' ? plan.priceMonthly : plan.priceAnnual}€
                      </span>
                      <span className={plan.featured ? 'text-gray-600' : 'text-white/70'}>
                        {t('landing.pricing.perMonthHT')}
                      </span>
                    </div>
                    <p className={`text-sm ${plan.featured ? 'text-lime-600 font-semibold' : 'text-lime-400'}`}>
                      {plan.savings}
                    </p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, fi) => (
                      <li key={fi} className="flex items-start gap-3">
                        <Check size={20} strokeWidth={2.5} className={`flex-shrink-0 mt-0.5 ${
                          plan.featured ? 'text-lime-600' : 'text-lime-500'
                        }`} />
                        <span className={`text-sm ${
                          plan.featured ? 'text-gray-700' : 'text-white/90'
                        } ${feature.includes('Tout') ? 'font-bold' : ''}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <button
                    onClick={handleSignupClick}
                    className={`w-full py-4 rounded-xl font-bold transition-all ${
                      plan.featured
                        ? 'bg-gradient-lime text-teal-600 shadow-lime-lg hover:shadow-lime hover:scale-105'
                        : 'bg-white/20 backdrop-blur-md text-white hover:bg-white/30 border border-white/30'
                    }`}
                  >
                    {plan.cta}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16 fade-up-element opacity-0 translate-y-8 transition-all duration-700">
            <h2 className="text-4xl md:text-5xl font-bold text-teal-600 mb-4">
              {cms('testimonials', 'title', 'landing.testimonials.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {cms('testimonials', 'subtitle', 'landing.testimonials.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonialsItems.map((testimonial, i) => (
              <div
                key={i}
                className="fade-up-element opacity-0 translate-y-8 transition-all duration-700 bg-gray-50 rounded-3xl p-8 border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all"
                style={{transitionDelay: `${i * 100}ms`}}
              >
                {/* Stars */}
                <div className="flex gap-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, si) => (
                    <Star key={si} size={18} fill="#D4FF00" color="#D4FF00" strokeWidth={0} />
                  ))}
                </div>

                {/* Text */}
                <p className="text-gray-700 leading-relaxed mb-6">
                  "{testimonial.text}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-teal flex-shrink-0" />
                  <div>
                    <div className="font-bold text-teal-600">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-gradient-teal relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src={teamData.bgImage}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Image principale */}
            <div className="fade-up-element opacity-0 translate-y-8 transition-all duration-700">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={teamData.mainImage}
                  alt={t('landing.team.teamImageAlt')}
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-teal-900/50 to-transparent" />
              </div>
            </div>

            {/* Texte */}
            <div className="text-white fade-up-element opacity-0 translate-y-8 transition-all duration-700" style={{transitionDelay: '200ms'}}>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                {cms('team', 'title', 'landing.team.title')}
              </h2>
              <p className="text-xl text-white/80 mb-8 leading-relaxed">
                {cms('team', 'description', 'landing.team.description')}
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-4xl font-bold text-lime-500 mb-2">{teamData.supportTime}</div>
                  <div className="text-white/70">{teamData.supportLabel}</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-lime-500 mb-2">{teamData.clientsCount}</div>
                  <div className="text-white/70">{teamData.clientsLabel}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 bg-gradient-teal text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,255,0,0.08),transparent_70%)]" />

        <div className="max-w-4xl mx-auto px-6 md:px-8 text-center relative z-10">
          <div className="fade-up-element opacity-0 translate-y-8 transition-all duration-700">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {cms('cta', 'title', 'landing.cta.title')}
            </h2>
            <p className="text-2xl text-white/80 mb-12">
              {cms('cta', 'subtitle', 'landing.cta.subtitle')}
            </p>

            <div className="flex flex-wrap justify-center gap-8 mb-12">
              {[
                t('landing.cta.freeTrial'),
                t('landing.cta.noCard'),
                t('landing.cta.cancelAnytime')
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-2 text-lg">
                  <Check size={24} strokeWidth={2.5} className="text-lime-500" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <button
              onClick={handleSignupClick}
              className="px-12 py-5 rounded-xl bg-gradient-lime text-teal-600 font-bold text-xl shadow-lime-lg hover:shadow-lime hover:scale-105 transition-all flex items-center gap-3 mx-auto"
            >
              {cms('cta', 'buttonText', 'landing.cta.startNow')}
              <ArrowRight size={24} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-lime flex items-center justify-center shadow-lime">
                  <FileText size={20} strokeWidth={2.5} className="text-teal-600" />
                </div>
                <span className="text-xl font-bold">FacturXPro</span>
              </div>
              <p className="text-white/70">
                {cms('footer', 'tagline', 'landing.footer.tagline')}
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-bold mb-4">{t('landing.footer.product')}</h4>
              <div className="space-y-2">
                <a href="#features" className="block text-white/70 hover:text-lime-500 transition-colors">{t('landing.footer.featuresLink')}</a>
                <a href="#pricing" className="block text-white/70 hover:text-lime-500 transition-colors">{t('landing.footer.pricingLink')}</a>
                <a href="#features" className="block text-white/70 hover:text-lime-500 transition-colors">{t('landing.footer.integrationsLink')}</a>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4">{t('landing.footer.company')}</h4>
              <div className="space-y-2">
                <a href="#features" className="block text-white/70 hover:text-lime-500 transition-colors">{t('landing.footer.about')}</a>
                <a href="/blog" className="block text-white/70 hover:text-lime-500 transition-colors">{t('landing.footer.blog')}</a>
                <a href="/careers" className="block text-white/70 hover:text-lime-500 transition-colors">{t('landing.footer.careers')}</a>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4">{t('landing.footer.support')}</h4>
              <div className="space-y-2">
                <a href="mailto:support@facturxpro.com" className="block text-white/70 hover:text-lime-500 transition-colors">{t('landing.footer.helpCenter')}</a>
                <a href="mailto:contact@facturxpro.com" className="block text-white/70 hover:text-lime-500 transition-colors">{t('landing.footer.contact')}</a>
                <a href="/status" className="block text-white/70 hover:text-lime-500 transition-colors">{t('landing.footer.statusLink')}</a>
              </div>
            </div>
          </div>

          {/* Bottom */}
          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/70">{cms('footer', 'copyright', 'landing.footer.copyright')}</p>
            <div className="flex gap-6">
              <a href="/legal" className="text-white/70 hover:text-lime-500 transition-colors">{t('landing.footer.legalNotice')}</a>
              <a href="/privacy" className="text-white/70 hover:text-lime-500 transition-colors">{t('landing.footer.privacy')}</a>
              <a href="/terms" className="text-white/70 hover:text-lime-500 transition-colors">{t('landing.footer.terms')}</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
